/**
 * Bulk Import Audit — dedicated forensic page (supersedes the old modal).
 *
 * Two-pane layout:
 *   • Left (30%): batch list with filename, upload date, activity summary,
 *     and a search box. Deep-linked via ?batch_id=<X>.
 *   • Right (70%): per-day grouped event timeline for the selected batch
 *     with action-type filter chips.
 *
 * Access: admin + super-admin. Sub-admins are blocked (enforced at both
 * the frontend gate and every backend endpoint).
 * Retention: default 10 batches with "Show more" pagination.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft,
  ClipboardList,
  Search,
  FileText,
  User as UserIcon,
  Calendar,
  Filter,
  Users,
  Info,
  Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Compact IST display — audit is India-facing, avoid UTC surprises.
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  } catch { return iso; }
};
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  } catch { return iso; }
};
const fmtRelative = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

// Human labels for canonical action strings (match services/bulk_import_audit.py)
const ACTION_META = {
  upload_csv: { label: 'Uploaded', color: 'bg-blue-100 text-blue-800' },
  keep_approve: { label: 'Approved → Live DB', color: 'bg-emerald-100 text-emerald-800' },
  skip_soft_delete: { label: 'Skip → Trash', color: 'bg-red-100 text-red-800' },
  merge_s2s: { label: 'Merged (staging)', color: 'bg-purple-100 text-purple-800' },
  merge_s2a: { label: 'Merged → Approved', color: 'bg-teal-100 text-teal-800' },
  edit_staging: { label: 'Edited row', color: 'bg-slate-100 text-slate-800' },
  bulk_edit_staging: { label: 'Bulk edit', color: 'bg-slate-100 text-slate-800' },
  restore_from_trash: { label: 'Restored', color: 'bg-amber-100 text-amber-800' },
  trash_purge: { label: 'Purged', color: 'bg-red-200 text-red-900' },
};

const actionLabel = (a) => ACTION_META[a]?.label || a;
const actionColor = (a) => ACTION_META[a]?.color || 'bg-gray-100 text-gray-700';

const PAGE_SIZE = 10;

// -----------------------------------------------------------------------
// Left pane: batch list
// -----------------------------------------------------------------------
const BatchListItem = ({ batch, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-3 py-2.5 border-b border-slate-200 hover:bg-slate-50 transition-colors ${
      active ? 'bg-indigo-50 border-l-4 border-l-indigo-600 pl-2' : 'border-l-4 border-l-transparent'
    }`}
    data-testid={`audit-batch-item-${batch.batch_id.slice(0, 8)}`}
  >
    <div className="flex items-start gap-2">
      <FileText className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold truncate ${active ? 'text-indigo-900' : 'text-slate-800'}`} title={batch.filename}>
          {batch.filename}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {fmtDate(batch.uploaded_at)}
          {batch.uploaded_by_email && (
            <>
              <span className="mx-1">·</span>
              <span className="truncate">{batch.uploaded_by_email.split('@')[0]}</span>
            </>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
          <span>{batch.event_count} event{batch.event_count !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span className="flex items-center gap-0.5">
            <Users className="w-3 h-3" />
            {batch.distinct_admins}
          </span>
          {batch.last_activity_at && batch.event_count > 0 && (
            <>
              <span>·</span>
              <span className="italic" title={fmtDateTime(batch.last_activity_at)}>
                {fmtRelative(batch.last_activity_at)}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  </button>
);

// -----------------------------------------------------------------------
// Right pane: event timeline
// -----------------------------------------------------------------------
const EventGroup = ({ dateLabel, events }) => (
  <div className="mb-4" data-testid="event-day-group">
    <div className="text-[11px] font-semibold uppercase text-slate-500 tracking-wider border-b border-slate-200 pb-1 mb-2">
      {dateLabel}
    </div>
    <ul className="space-y-2">
      {events.map((e, i) => (
        <li key={e.id || i} className="flex items-start gap-2 text-sm">
          <span
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${actionColor(e.action)} flex-shrink-0 mt-0.5`}
            title={e.action}
          >
            {actionLabel(e.action)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-slate-800">
              <span className="font-medium">
                {(e.user_email || 'unknown').split('@')[0]}
              </span>
              {e.counts && Object.keys(e.counts).length > 0 && (
                <span className="ml-2 text-slate-500 text-[12px]">
                  ({Object.entries(e.counts).map(([k, v]) => `${v} ${k}`).join(', ')})
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500">
              {new Date(e.timestamp).toLocaleTimeString('en-IN', {
                hour: '2-digit', minute: '2-digit',
                timeZone: 'Asia/Kolkata',
              })}
              {e.metadata?.filename && e.action === 'upload_csv' && (
                <> · file: {e.metadata.filename}</>
              )}
              {e.staging_ids && e.staging_ids.length > 0 && (
                <> · {e.staging_ids.length} row{e.staging_ids.length !== 1 ? 's' : ''}</>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const RightPane = ({ batch, events, loading, actionFilter, onActionFilter, onCopyCsv }) => {
  // Group events by IST calendar date
  const groups = useMemo(() => {
    const g = {};
    const order = [];
    for (const e of events) {
      const day = new Date(e.timestamp).toLocaleDateString('en-CA', {
        timeZone: 'Asia/Kolkata',
      }); // YYYY-MM-DD, sortable
      if (!g[day]) { g[day] = []; order.push(day); }
      g[day].push(e);
    }
    // Reverse-chrono order — most recent day at the top
    order.sort((a, b) => b.localeCompare(a));
    return order.map((d) => ({
      day: d,
      label: fmtDate(new Date(d + 'T00:00:00Z').toISOString()),
      events: g[d],
    }));
  }, [events]);

  const uniqueActions = useMemo(
    () => Array.from(new Set(events.map((e) => e.action))).sort(),
    [events],
  );

  if (!batch) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
        <div className="text-center">
          <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Select a batch from the left to view its audit trail.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      {/* Batch header */}
      <div className="px-5 py-3 border-b bg-white flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            <span className="truncate" title={batch.filename}>{batch.filename}</span>
          </h2>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Uploaded {fmtDateTime(batch.uploaded_at)}
            </span>
            {batch.uploaded_by_email && (
              <>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <UserIcon className="w-3 h-3" /> {batch.uploaded_by_email}
                </span>
              </>
            )}
            {batch.grade && (<><span>·</span><span>{batch.grade}</span></>)}
            {batch.mmd_location && (<><span>·</span><span>{batch.mmd_location}</span></>)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
            batch: {batch.batch_id.slice(0, 8)}…
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onCopyCsv}
          disabled={events.length === 0}
          data-testid="audit-copy-csv-btn"
        >
          <Download className="w-4 h-4 mr-1" />
          Copy as CSV
        </Button>
      </div>

      {/* Filter chips */}
      {uniqueActions.length > 1 && (
        <div className="px-5 py-2 border-b bg-slate-50 flex items-center gap-1.5 flex-wrap text-[11px]">
          <Filter className="w-3 h-3 text-slate-500" />
          <button
            onClick={() => onActionFilter(null)}
            className={`px-2 py-0.5 rounded border transition ${
              !actionFilter
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-800 border-slate-300 hover:bg-slate-100'
            }`}
            data-testid="audit-filter-all"
          >
            All ({events.length})
          </button>
          {uniqueActions.map((a) => (
            <button
              key={a}
              onClick={() => onActionFilter(a)}
              className={`px-2 py-0.5 rounded border transition ${
                actionFilter === a
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-800 border-slate-300 hover:bg-slate-100'
              }`}
              data-testid={`audit-filter-${a}`}
            >
              {actionLabel(a)}
            </button>
          ))}
        </div>
      )}

      {/* Events */}
      <div className="flex-1 overflow-y-auto px-5 py-3">
        {loading ? (
          <div className="text-center py-8 text-sm text-slate-500">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500">
            {actionFilter
              ? `No "${actionLabel(actionFilter)}" events for this batch.`
              : 'No events recorded for this batch.'}
          </div>
        ) : (
          groups.map((g) => (
            <EventGroup key={g.day} dateLabel={g.label} events={g.events} />
          ))
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------
const BulkImportAuditPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [batches, setBatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchQ, setSearchQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const selectedBatchId = searchParams.get('batch_id');
  const [batchDetail, setBatchDetail] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [actionFilter, setActionFilter] = useState(null);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  // Gate: admin + super-admin only. Sub-admins are blocked (product
  // decision). Redirect them to /admin so they don't hit a blank screen.
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Bulk Import Audit is not available to sub-admins.');
      navigate('/admin');
    }
  }, [user, navigate]);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Load batches list
  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const params = new URLSearchParams({ limit: String(pageSize), offset: '0' });
      if (debouncedQ) params.set('q', debouncedQ);
      const r = await axios.get(
        `${API}/admin/bulk-import/audit-batches?${params}`,
        { headers: authHeaders },
      );
      setBatches(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load audit batches');
    } finally {
      setLoadingBatches(false);
    }
  }, [pageSize, debouncedQ, authHeaders]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // Load events for selected batch
  useEffect(() => {
    if (!selectedBatchId) {
      setBatchDetail(null); setEvents([]); return;
    }
    let cancelled = false;
    setLoadingEvents(true);
    (async () => {
      try {
        const params = new URLSearchParams({ limit: '500', offset: '0' });
        if (actionFilter) params.set('action_type', actionFilter);
        const r = await axios.get(
          `${API}/admin/bulk-import/audit-batches/${selectedBatchId}/events?${params}`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        setBatchDetail(r.data.batch);
        setEvents(r.data.items || []);
      } catch {
        if (!cancelled) toast.error('Failed to load batch events');
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBatchId, actionFilter, authHeaders]);

  // If the user hasn't selected a batch yet but batches loaded, auto-select
  // the first one so the right pane isn't empty on landing.
  useEffect(() => {
    if (!selectedBatchId && batches.length > 0) {
      setSearchParams({ batch_id: batches[0].batch_id }, { replace: true });
    }
  }, [batches, selectedBatchId, setSearchParams]);

  const handleSelectBatch = (batchId) => {
    setActionFilter(null);
    setSearchParams({ batch_id: batchId });
  };

  const handleCopyCsv = () => {
    if (events.length === 0) return;
    const header = 'timestamp_ist,user,action,counts,staging_ids_count';
    const rows = events.map((e) => {
      const ts = new Date(e.timestamp).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
      });
      const counts = Object.entries(e.counts || {})
        .map(([k, v]) => `${k}=${v}`).join(';');
      return [
        `"${ts}"`,
        `"${e.user_email || ''}"`,
        `"${actionLabel(e.action)}"`,
        `"${counts}"`,
        `${(e.staging_ids || []).length}`,
      ].join(',');
    });
    const csv = [header, ...rows].join('\n');
    try {
      navigator.clipboard.writeText(csv);
      toast.success(`Copied ${events.length} row(s) as CSV`);
    } catch {
      toast.error('Copy failed — clipboard permission denied?');
    }
  };

  const canShowMore = batches.length < total;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <div className="bg-white border-b px-5 py-3 flex items-center gap-3">
        <Link
          to="/admin"
          className="text-slate-600 hover:text-slate-900 flex items-center gap-1 text-sm"
          data-testid="audit-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-indigo-600" />
          Bulk Import Audit
        </h1>
        <span className="text-xs text-slate-500 ml-2 hidden md:inline">
          Every mutating action on every batch · 90-day retention
        </span>
      </div>

      {/* Two-pane layout */}
      <div className="flex" style={{ height: 'calc(100vh - 57px)' }}>
        {/* Left pane */}
        <div className="w-[30%] min-w-[280px] max-w-[420px] bg-white border-r flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search filename…"
                className="pl-8 h-9 text-sm"
                data-testid="audit-search-input"
              />
            </div>
            <div className="text-[11px] text-slate-500">
              Showing {batches.length} of {total} batch{total !== 1 ? 'es' : ''}
              {debouncedQ && <> matching &quot;{debouncedQ}&quot;</>}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingBatches && batches.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 text-center">Loading…</div>
            ) : batches.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 text-center">
                {debouncedQ
                  ? 'No batches match your search.'
                  : 'No batches recorded yet.'}
              </div>
            ) : (
              batches.map((b) => (
                <BatchListItem
                  key={b.batch_id}
                  batch={b}
                  active={b.batch_id === selectedBatchId}
                  onClick={() => handleSelectBatch(b.batch_id)}
                />
              ))
            )}
          </div>

          {canShowMore && (
            <div className="p-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageSize((s) => s + PAGE_SIZE)}
                className="w-full"
                data-testid="audit-load-more-btn"
              >
                Show {Math.min(PAGE_SIZE, total - batches.length)} more
              </Button>
            </div>
          )}
        </div>

        {/* Right pane */}
        <RightPane
          batch={batchDetail}
          events={events}
          loading={loadingEvents}
          actionFilter={actionFilter}
          onActionFilter={setActionFilter}
          onCopyCsv={handleCopyCsv}
        />
      </div>

      {/* Sub-admin gate note (also enforced backend) */}
      <div className="hidden">
        <Info />
      </div>
    </div>
  );
};

export default BulkImportAuditPage;
